import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Anchor, Badge, Button, Group, Select, Stack, Text, Tooltip } from '@mantine/core'
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconExternalLink,
  IconPlus,
  IconRosetteDiscountCheck,
  IconTrash,
} from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import { ActivateModal } from './ActivateModal'
import {
  DOCUMENT_STATUS_COLORS,
  UNIT_GRADE_COLORS,
  UNIT_KIND_COLORS,
  actorLabel,
  unitPopulationLabel,
  useCpgDocument,
  useCpgFamilies,
  type CpgAssignment,
  type CpgDocumentDetail,
  type CpgDocumentStatus,
  type CpgUnitView,
} from './hooks'

export function CpgStatusBadge({ status }: { status: CpgDocumentStatus }) {
  return (
    <Badge color={DOCUMENT_STATUS_COLORS[status]} variant="light" radius="sm">
      {status.replace('_', ' ')}
    </Badge>
  )
}

function formatPopulation(ageMin: number | null, ageMax: number | null): string {
  if (ageMin === null && ageMax === null) return 'all ages'
  if (ageMin !== null && ageMax !== null) return `ages ${ageMin}–${ageMax}`
  if (ageMin !== null) return `ages ${ageMin}+`
  return `up to age ${ageMax}`
}

function DocumentRefLine({
  label,
  doc,
}: {
  label: string
  doc: NonNullable<CpgDocumentDetail['supersedes']>
}) {
  return (
    <Text size="sm">
      <span className="text-gray-500">{label}:</span> {doc.title} · v{doc.version} · #{doc.id}{' '}
      <CpgStatusBadge status={doc.status} />
    </Text>
  )
}

/**
 * One guideline unit: kind, source grade, normalized strength, population requirements
 * (age/sex/risk flags) in the header; body behind a native disclosure. A `highlighted` unit
 * (the passage a measure citation deep-links to) opens, is marked, and scrolls into view.
 */
function UnitItem({ unit, highlighted }: { unit: CpgUnitView; highlighted: boolean }) {
  const population = unitPopulationLabel(unit)
  const ref = useRef<HTMLDetailsElement>(null)
  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView?.({ block: 'center' })
  }, [highlighted])
  return (
    <details
      ref={ref}
      open={highlighted || undefined}
      className={
        highlighted
          ? 'rounded border-2 border-yellow-400 bg-yellow-50 px-3 py-2'
          : 'rounded border border-gray-200 px-3 py-2'
      }
    >
      <summary className="cursor-pointer select-none">
        {highlighted && (
          <Badge
            color="yellow"
            variant="filled"
            radius="sm"
            size="sm"
            className="mr-2 align-middle"
          >
            Cited passage
          </Badge>
        )}
        <Badge
          color={UNIT_KIND_COLORS[unit.kind]}
          variant="light"
          radius="sm"
          size="sm"
          className="mr-2 align-middle"
        >
          {unit.kind}
        </Badge>
        {unit.gradeOriginal && (
          <Badge variant="outline" color="dark" radius="sm" size="sm" className="mr-2 align-middle">
            Grade {unit.gradeOriginal}
          </Badge>
        )}
        {unit.grade && (
          <Badge
            color={UNIT_GRADE_COLORS[unit.grade]}
            variant="light"
            radius="sm"
            size="sm"
            className="mr-2 align-middle"
          >
            {unit.grade}
          </Badge>
        )}
        <span className="text-sm text-gray-600">{unit.sectionPath ?? ''}</span>
        {population && (
          <span className="ml-2 text-xs whitespace-nowrap text-gray-500">{population}</span>
        )}
      </summary>
      <Text size="sm" className="mt-2 whitespace-pre-wrap">
        {unit.body}
      </Text>
    </details>
  )
}

function AssignmentRow({
  assignment,
  canEdit,
  busy,
  onConfirm,
  onRemove,
}: {
  assignment: CpgAssignment
  canEdit: boolean
  busy: boolean
  onConfirm: () => void
  onRemove: () => void
}) {
  const confirmed = assignment.confirmedBy !== null
  return (
    <div className="rounded border border-gray-200 px-3 py-2">
      <Group gap="xs" wrap="nowrap">
        <Badge variant={confirmed ? 'filled' : 'default'} color="blue" radius="sm">
          {assignment.slug}
        </Badge>
        <Text size="sm" fw={500} className="truncate">
          {assignment.label}
        </Text>
        <Badge
          size="xs"
          variant="outline"
          color={assignment.assignedVia === 'ai' ? 'grape' : 'teal'}
        >
          {assignment.assignedVia === 'ai' ? 'AI' : 'manual'}
        </Badge>
        <span className="grow" />
        {confirmed ? (
          <Tooltip label={`Confirmed by ${actorLabel(assignment.confirmedBy)}`}>
            <Badge
              size="sm"
              variant="light"
              color="green"
              leftSection={<IconRosetteDiscountCheck size={12} />}
            >
              confirmed
            </Badge>
          </Tooltip>
        ) : (
          canEdit && (
            <Button size="compact-xs" loading={busy} onClick={onConfirm}>
              Confirm
            </Button>
          )
        )}
        {canEdit && (
          <Button
            size="compact-xs"
            variant="subtle"
            color="red"
            disabled={busy}
            onClick={onRemove}
            aria-label={`Remove ${assignment.slug}`}
          >
            <IconTrash size={14} />
          </Button>
        )}
      </Group>
      {assignment.rationale && (
        <Text size="xs" c="dimmed" fs="italic" className="mt-1">
          {assignment.rationale}
        </Text>
      )}
    </div>
  )
}

interface DocumentDetailProps {
  id: number | null
  /** Fired after a mutation that changes list rows (a status flip) — reload tables. */
  onMutated?: () => void
  /** Unit to open, mark and scroll to — a measure citation's deep link (`?unit=<id>`). */
  highlightUnitId?: number | null
}

/**
 * The right-hand document panel shared by the Review Queue and Documents views:
 * provenance header, family assignments (confirm / add / remove while the document is
 * needs_registration or active), succession links, the verbatim units, and Activate — the
 * only human lifecycle action. See docs/cpg/CPG_ADMIN.md.
 */
export function DocumentDetail({ id, onMutated, highlightUnitId = null }: DocumentDetailProps) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const query = useCpgDocument(id)
  const familiesQuery = useCpgFamilies()
  const [addFamilyId, setAddFamilyId] = useState<string | null>(null)
  const [activateOpen, setActivateOpen] = useState(false)

  const doc = query.data?.data

  const refreshDocument = async () => {
    await queryClient.invalidateQueries({ queryKey: orpc.getCpgDocument.key() })
  }

  const familyAction = useMutation(
    orpc.cpgDocumentFamilyAction.mutationOptions({ onSuccess: refreshDocument }),
  )

  // Families still offerable in the Add select: active and not already assigned.
  const addOptions = useMemo(() => {
    const groups = familiesQuery.data?.data ?? []
    const assigned = new Set((doc?.assignments ?? []).map((a) => a.familyId))
    return groups
      .map((g) => ({
        group: g.label,
        items: g.families
          .filter((f) => f.active && !assigned.has(f.id))
          .map((f) => ({ value: String(f.id), label: `${f.label} (${f.slug})` })),
      }))
      .filter((g) => g.items.length > 0)
  }, [familiesQuery.data, doc?.assignments])

  if (id === null) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-500">
        Select a document to review.
      </div>
    )
  }
  if (query.isLoading || !doc) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-500">
        {query.error ? `Failed to load document ${id}.` : 'Loading…'}
      </div>
    )
  }

  const canEdit = doc.status === 'needs_registration' || doc.status === 'active'
  const unconfirmed = doc.assignments.filter((a) => a.confirmedBy === null).length

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-4">
      {/* Header */}
      <div>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <Text fw={600} className="grow">
            {doc.title}
          </Text>
          <CpgStatusBadge status={doc.status} />
        </Group>
        <Text size="sm" c="dimmed" className="mt-1">
          {doc.sourceName} · v{doc.version}
          {doc.publishedAt ? ` · published ${doc.publishedAt}` : ''} · {doc.externalId} · #{doc.id}{' '}
          ·{' '}
          <Anchor href={doc.url} target="_blank" rel="noreferrer" size="sm">
            source <IconExternalLink size={12} className="inline align-middle" />
          </Anchor>
        </Text>
        <Text size="sm" c="dimmed">
          Population: {formatPopulation(doc.ageMin, doc.ageMax)}
        </Text>
      </div>

      {doc.status === 'withdrawn' && (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
          The publisher removed this guideline from the corpus
          {doc.lastSeenAt ? ` (last seen ${new Date(doc.lastSeenAt).toLocaleDateString()})` : ''}.
          It is excluded from reports, and there is nothing to do here — if the publisher restores
          it unchanged, the next corpus sync makes it active again on its own.
        </Alert>
      )}

      {/* Succession */}
      {(doc.supersedes || doc.supersededBy || doc.activationPredecessor) && (
        <Stack gap={2}>
          {doc.supersededBy && <DocumentRefLine label="Superseded by" doc={doc.supersededBy} />}
          {doc.supersedes && <DocumentRefLine label="Supersedes" doc={doc.supersedes} />}
          {doc.status === 'needs_registration' && doc.activationPredecessor && (
            <DocumentRefLine label="Will supersede on activation" doc={doc.activationPredecessor} />
          )}
        </Stack>
      )}

      {/* Family assignments */}
      <div>
        <Group justify="space-between" className="mb-2">
          <Text size="sm" fw={600} tt="uppercase" c="dimmed">
            Families ({doc.assignments.length})
          </Text>
          {canEdit && unconfirmed > 0 && (
            <Button
              size="compact-xs"
              variant="light"
              leftSection={<IconCheck size={12} />}
              loading={familyAction.isPending}
              onClick={() => familyAction.mutate({ id: doc.id, action: 'confirm_all' })}
            >
              Confirm all ({unconfirmed})
            </Button>
          )}
        </Group>
        <Stack gap="xs">
          {doc.assignments.length === 0 && (
            <Text size="sm" c="dimmed">
              No families assigned — the classifier found none, or they were removed.
            </Text>
          )}
          {doc.assignments.map((a) => (
            <AssignmentRow
              key={a.familyId}
              assignment={a}
              canEdit={canEdit}
              busy={familyAction.isPending}
              onConfirm={() =>
                familyAction.mutate({ id: doc.id, action: 'confirm', familyId: a.familyId })
              }
              onRemove={() =>
                familyAction.mutate({ id: doc.id, action: 'remove', familyId: a.familyId })
              }
            />
          ))}
          {canEdit && (
            <Group gap="xs">
              <Select
                placeholder="Add family…"
                data={addOptions}
                value={addFamilyId}
                onChange={setAddFamilyId}
                searchable
                clearable
                size="xs"
                className="w-72"
              />
              <Button
                size="compact-sm"
                variant="light"
                leftSection={<IconPlus size={12} />}
                disabled={addFamilyId === null}
                loading={familyAction.isPending}
                onClick={() => {
                  if (addFamilyId === null) return
                  familyAction.mutate(
                    { id: doc.id, action: 'add', familyId: Number(addFamilyId) },
                    { onSuccess: () => setAddFamilyId(null) },
                  )
                }}
              >
                Add
              </Button>
              {/* Right-aligned on the same row, under the per-family Confirm buttons. */}
              {doc.status === 'needs_registration' && (
                <Tooltip
                  label={`${unconfirmed} unconfirmed suggestion(s) — confirm or remove them first`}
                  disabled={unconfirmed === 0}
                >
                  <Button
                    className="ml-auto"
                    disabled={unconfirmed > 0}
                    onClick={() => setActivateOpen(true)}
                  >
                    Activate…
                  </Button>
                </Tooltip>
              )}
            </Group>
          )}
        </Stack>
        {familyAction.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} className="mt-2">
            {familyAction.error.message}
          </Alert>
        )}
      </div>

      {/* Content units */}
      <div>
        <Text size="sm" fw={600} tt="uppercase" c="dimmed" className="mb-2">
          Content ({doc.units.length} units)
        </Text>
        <Stack gap="xs">
          {doc.units.map((unit) => (
            <UnitItem key={unit.id} unit={unit} highlighted={unit.id === highlightUnitId} />
          ))}
        </Stack>
      </div>

      {activateOpen && (
        <ActivateModal
          doc={doc}
          onClose={() => setActivateOpen(false)}
          onActivated={async () => {
            await refreshDocument()
            setActivateOpen(false)
            onMutated?.()
          }}
        />
      )}
    </div>
  )
}
